<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catTeal" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-clipboard-list-outline" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="catTeal"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ cids.length }}
      </v-chip>
      <v-tooltip v-if="pendingCount > 0" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            x-small
            color="orange darken-2"
            text-color="white"
            class="ml-1 flex-shrink-0"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon x-small left>mdi-alert-outline</v-icon>
            {{ pendingCount }}
          </v-chip>
        </template>
        <span>{{ pendingCount }} sem medicamento vinculado</span>
      </v-tooltip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar CID / outras doenças</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-off-outline</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhum CID registrado ainda.
        </p>
        <v-btn small color="catTeal" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Adicionar CID
        </v-btn>
      </div>

      <!-- Lista de CIDs -->
      <div v-else class="cids-list">
        <v-chip
          v-for="cid in cids"
          :key="cid.id"
          class="cid-chip"
          color="#ECFEFF"
          text-color="catTeal"
          label
        >
          <strong v-if="cid.code" class="mr-1">{{ cid.code }}</strong>
          <span class="chip-text">{{ cid.description }}</span>
        </v-chip>
      </div>
    </v-card-text>

    <EditCidsDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :dialog-title="`CIDs do paciente`"
      :cids="cids"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getConditionsByPatient } from "@/services/conditionService";
import { getPendencias } from "@/services/correlationService";
import EditCidsDialog from "@/features/patients/profile-dashboard/dialogs/EditCidsDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "CidsCard",

  components: {
    EditCidsDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      dialogVisible: false,
      cids: [],
      pendingCount: 0,
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.cids || []).length;
    },
    hasData() {
      return this.cids.length > 0;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        // Card "mestre": mostra TODAS as condições do paciente (qualquer category).
        // Os cards de Crônicas/Transtornos são filtros que destacam o seu assunto.
        this.cids = await dashboardStore.section(this.patientId, "conditions", () => getConditionsByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar CIDs:", error);
        this.cids = [];
      }
      // Pendências: doenças sem medicamento vinculado (e sem "nenhum medicamento").
      try {
        const p = await getPendencias(this.patientId);
        this.pendingCount = (p.conditions_without_medication || []).length;
      } catch (error) {
        this.pendingCount = 0;
      }
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Ciano / Teal (CIDs) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #a5f3fc !important;
  box-shadow: 0 2px 10px rgba(14, 116, 144, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #67e8f9 !important;
  box-shadow: 0 6px 20px rgba(14, 116, 144, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: linear-gradient(135deg, var(--ds-teal-bg) 0%, #cffafe 100%);
  border-bottom: 1px solid #a5f3fc;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-teal);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #67e8f9;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-teal);
}

.cids-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cid-chip {
  font-size: 12px !important;
  font-weight: 600 !important;
  height: auto !important;
  min-height: 38px !important;
  padding: 8px 12px !important;
  white-space: normal !important;
  overflow-wrap: break-word;
  word-break: normal;
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 10px !important;
}

.chip-text {
  flex: 1;
  text-align: left;
  line-height: 1.4;
}
</style>
